import { useEffect, useRef } from "react";

/** How often the task list re-checks connector state when nothing is known to be syncing. */
export const IDLE_CONNECTOR_REFRESH_MS = 30 * 1000;

/**
 * Keep the connector list fresh while the task list sits idle, and when the tab/app regains
 * focus. A background run (scheduled sync, webhook-triggered Granola note) that starts while
 * nobody is polling would otherwise go unnoticed. Once a refresh sees a sync, the existing
 * 5s list poll takes over, and the sidebar context refetches the task list when it finishes.
 *
 * @example
 * useIdleConnectorRefresh({ refreshWorkflows, enabled: !isLandingDemo });
 * // refreshWorkflows() every 30s while the tab is visible, and on visibilitychange
 */
export const useIdleConnectorRefresh = ({
  refreshWorkflows,
  enabled = true,
  intervalMs = IDLE_CONNECTOR_REFRESH_MS,
}) => {
  const refreshRef = useRef(refreshWorkflows);
  refreshRef.current = refreshWorkflows;

  useEffect(() => {
    if (!enabled) return undefined;
    const refresh = () => {
      if (document.hidden) return;
      Promise.resolve(refreshRef.current?.()).catch(() => {
        // Keep the stale list on transient failures; the next tick retries.
      });
    };
    const timerId = window.setInterval(refresh, intervalMs);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timerId);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [enabled, intervalMs]);
};
